import { FC, useMemo } from "react";
import { CartesianGrid, Legend, Line, LineChart, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { SKUAnalytics, TagAnalytics } from "@/data/types";
import { formatByPeriod } from "@/lib/utils";

const chartConfig = {
  total_duration: {
    label: "Total Duration",
  },
  average_duration: {
    label: "Average Duration",
  },
} satisfies ChartConfig;

type Props = {
  data?: SKUAnalytics | TagAnalytics;
};

export const SessionDurationChart: FC<Props> = ({ data }) => {
  const chartData = useMemo(() => {
    const googleAnalytics = data?.google_analytics;

    if (!googleAnalytics) {
      return [];
    }

    return Object.keys(googleAnalytics.total_duration_data).map((time) => {
      return {
        time,
        totalDuration: googleAnalytics.total_duration_data[time] / 60,
        averageDuration: googleAnalytics.average_duration_data[time] / 60,
      };
    });
  }, [data]);

  // Format minutes as "Xm" for Y-axis ticks
  const formatMinutes = (value: number) => {
    if (value < 1) return "<1m";
    return `${Math.floor(value)}m`;
  };

  // Format minutes as "Xm Ys" for tooltips
  const formatMinutesPrecise = (value: number) => {
    const minutes = Math.floor(value);
    const seconds = Math.round((value % 1) * 60);
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  };

  return (
    <Card className="flex-1">
      <CardHeader>
        <CardTitle>Session Duration</CardTitle>
        <CardDescription>
          Total and average session duration over time
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[240px] w-full">
          <LineChart accessibilityLayer data={chartData}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="time"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tickFormatter={(value) => formatByPeriod(value, data?.period)}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={formatMinutes}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) =>
                    formatByPeriod(value, data?.period)
                  }
                  formatter={(value) => formatMinutesPrecise(value as number)}
                />
              }
            />

            <Line
              name="Average"
              dataKey="averageDuration"
              type="monotone"
              stroke="var(--tailwind-colors-blue-400, #60A5FA)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              name="Total"
              dataKey="totalDuration"
              type="monotone"
              stroke="var(--tailwind-colors-blue-700, #1D4ED8)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Legend iconSize={8} iconType="square" verticalAlign="bottom" />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
